import * as React from 'react';

import {exhaustivenessCheck} from 'nutshell-core/typing/exhaustiveness-check';

import {
    BoxRegularIcon,
    CompetitorRegularIcon,
    SourceRegularIcon,
    InvoiceRegularDiagonalIcon,
    QuoteRegularIcon,
    FileIcon,
    ActivityIcon,
    NavIconAutomationIcon,
    PencilToPaperRegularIcon,
    LongTextIcon,
    CalendarIcon,
    NavIconSalesIcon,
    LocationPinIcon,
    LocationPinRegularIcon,
    PhoneIcon,
    PhoneRegularIcon,
    EmailIcon,
    EnvelopeRegularIcon,
    GlobeRegularIcon,
    NavIconWebsiteIcon,
    CheckIcon,
    SelectMultiIcon,
    TargetArrowRegularIcon,
    EnvelopeGroupRegularIcon,
    EnvelopeRegularUserIcon,
    DripsRegularIcon,
    FormRegularCheckIcon,
    LeadChevronRegularIcon,
    PeopleRegularIcon,
    PersonCircleRegularIcon,
} from 'shells/icon';
import {CompanyRegularIcon} from 'shells/icon/svg/company-regular-icon';
import {HashtagRegularIcon} from 'shells/icon/svg/hashtag-regular-icon';
import {ParentCompanyIcon} from 'shells/icon/svg/parent-company-icon';

import {IconAvatar} from '../avatar/icon-avatar';
import {colors} from '../colors/colors';
import {PrimaryIconWrapper} from '../primary-icon-wrapper/primary-icon-wrapper';

import type {RowType} from './types';

type Props = {
    type: RowType;
    isPrimary?: boolean;
};

export function RowIcon(props: Props) {
    const getIconInfo = (type: RowType) => {
        switch (type) {
            case 'products':
                return {
                    icon: BoxRegularIcon,
                    color: colors.lead,
                    backgroundColor: colors.greenLt,
                };
            case 'competitors':
                return {
                    icon: CompetitorRegularIcon,
                    color: colors.greyLt,
                    backgroundColor: colors.skeletonGrey,
                };
            case 'contacts':
                return {
                    icon: PersonCircleRegularIcon,
                    color: colors.person,
                    backgroundColor: colors.blueXlt,
                };
            case 'accounts':
                return {
                    icon: CompanyRegularIcon,
                    color: colors.company,
                    backgroundColor: colors.plumXlt,
                };
            // Kind of non-sensical, since these are always custom
            case 'channels':
                return {
                    icon: SourceRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'sources':
                return {
                    icon: SourceRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'invoices':
                return {
                    icon: InvoiceRegularDiagonalIcon,
                    color: colors.lead,
                    backgroundColor: colors.greenLt,
                };
            case 'quotes':
                return {
                    icon: QuoteRegularIcon,
                    color: colors.white,
                    backgroundColor: colors.lead,
                };
            case 'attachments':
                return {
                    icon: FileIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'activities':
                return {
                    icon: ActivityIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'tasks':
                return {
                    icon: NavIconAutomationIcon,
                    color: colors.orange,
                    backgroundColor: colors.orangeLt,
                    isLegacyIcon: true,
                };
            case 'goals':
                return {
                    icon: TargetArrowRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-text':
                return {
                    icon: PencilToPaperRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'custom-field-long-text':
                return {
                    icon: LongTextIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-date':
                return {
                    icon: CalendarIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-currency':
                return {
                    icon: NavIconSalesIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-address':
                return {
                    icon: LocationPinIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'address':
                return {
                    icon: LocationPinRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'custom-field-phone':
                return {
                    icon: PhoneIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-email':
                return {
                    icon: EmailIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'email':
                return {
                    icon: EnvelopeRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'phone':
                return {
                    icon: PhoneRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'url':
                return {
                    icon: GlobeRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'custom-field-url':
                return {
                    icon: NavIconWebsiteIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-number':
                return {
                    icon: HashtagRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'custom-field-enum-single':
                return {
                    icon: CheckIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'custom-field-enum-multi':
                return {
                    icon: SelectMultiIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'personal-email-sequence':
                return {
                    icon: EnvelopeGroupRegularIcon,
                    color: colors.greyLt,
                    backgroundColor: colors.offWhiteTransparent,
                };
            case 'scheduled-email':
                return {
                    icon: EnvelopeRegularUserIcon,
                    color: colors.greyLt,
                    backgroundColor: colors.offWhiteTransparent,
                };
            case 'drip-sequence':
                return {
                    icon: DripsRegularIcon,
                    color: colors.greyLt,
                    backgroundColor: colors.offWhiteTransparent,
                };
            case 'goal':
                return {
                    icon: TargetArrowRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                    isLegacyIcon: true,
                };
            case 'form-submissions':
                return {
                    icon: FormRegularCheckIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'parent-account':
                return {
                    icon: ParentCompanyIcon,
                    color: colors.company,
                    backgroundColor: colors.plumXlt,
                };
            case 'leads':
                return {
                    icon: LeadChevronRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            case 'audience':
                return {
                    icon: PeopleRegularIcon,
                    color: colors.grey,
                    backgroundColor: colors.offWhiteDk,
                };
            default:
                exhaustivenessCheck(type);
        }
    };

    const iconInfo = getIconInfo(props.type);

    const IconAvatarComponent = (
        <IconAvatar
            icon={iconInfo.icon}
            avatarSize={32}
            iconSize={iconInfo.isLegacyIcon ? 16 : 20}
            iconColor={iconInfo.color}
            backgroundColor={iconInfo.backgroundColor}
            shape='square'
        />
    );

    if (props.isPrimary) {
        return <PrimaryIconWrapper>{IconAvatarComponent}</PrimaryIconWrapper>;
    }
    return IconAvatarComponent;
}
